import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { faThLarge, faTasks, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import type { SettingsSidebarProps } from './types';
import { styles } from './styles';
import { SidebarHeader } from './SidebarHeader';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarActionButton } from './SidebarActionButton';

const STORAGE_KEY = 'settings-sidebar-collapsed';

const readCollapsedState = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  userName,
  userRole,
  updateCount,
  categories,
  basePath,
  onLogout,
  onOpenUpdateLog,
}) => {
  const [collapsed, setCollapsed] = useState(readCollapsedState);
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      /* localStorage unavailable */
    }
  }, [collapsed]);

  const toggle = (): void => setCollapsed(prev => !prev);

  return (
    <nav
      className={clsx(
        styles.sidebar,
        collapsed ? styles.sidebarCollapsed : styles.sidebarExpanded,
      )}>
      <SidebarHeader
        userName={userName}
        userRole={userRole}
        collapsed={collapsed}
        onToggle={toggle}
      />

      <div className={styles.nav}>
        <div className={styles.section}>
          <div className={styles.navList}>
            <SidebarNavItem
              to="/"
              icon={faThLarge}
              label="Dashboard"
              isActive={pathname === '/'}
              collapsed={collapsed}
            />
          </div>
        </div>

        {categories.map(category => (
          <div key={category.label} className={styles.section}>
            {collapsed ? (
              <div className={styles.dividerCollapsed} />
            ) : (
              <span className={clsx(styles.categoryLabel, styles.categoryLabelVisible)}>
                {category.label}
              </span>
            )}

            <div className={styles.navList}>
              {category.items.map(item => {
                const fullPath = `${basePath}/${item.path}`;
                return (
                  <SidebarNavItem
                    key={item.path}
                    to={fullPath}
                    icon={item.icon}
                    label={item.label}
                    isActive={pathname.startsWith(fullPath)}
                    collapsed={collapsed}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <SidebarActionButton
          icon={faTasks}
          label="Update Log"
          collapsed={collapsed}
          onClick={onOpenUpdateLog}
          badge={updateCount}
        />
        <SidebarActionButton
          icon={faSignOutAlt}
          label="Logout"
          collapsed={collapsed}
          onClick={onLogout}
          variant="danger"
        />
      </div>
    </nav>
  );
};

export type {
  SettingsSidebarProps,
  NavItem,
  SidebarCategory,
  SidebarNavItemProps,
  SidebarActionButtonProps,
  SidebarHeaderProps,
} from './types';
export { SidebarNavItem } from './SidebarNavItem';
export { SidebarActionButton } from './SidebarActionButton';
export { SidebarHeader } from './SidebarHeader';
export default SettingsSidebar;
